import { useQueryClient } from '@tanstack/react-query';

import { RecognitionValuesResponse, useToast } from '@aslanpay/portal-common-lib';

import useFetchRecognitionValues from 'app/hooks/useFetchRecognitionValues';
import useFetchRecognitionCards from 'app/hooks/useFetchRecognitionCards';
import useRecognitionValueMutations from 'app/hooks/useRecognitionValueMutations';
import useRecognitionCardMutations from 'app/hooks/useRecognitionCardMutations';

import { uploadRecognitionCardImage } from 'app/utils/recognitionCardUpload';

import RecognitionValuesManager from './RecognitionValuesManager';
import { ManagedRecognitionValue, RecognitionValueFormState } from './types';

interface CompanyValuesSectionProps {
  companyId: string;
}

function CompanyValuesSection({ companyId }: CompanyValuesSectionProps) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const enabled = Boolean(companyId);
  const { data: valuesData, isInitialLoading: isLoadingValues } = useFetchRecognitionValues(
    { companyId },
    enabled
  );
  const { data: cardsData, isInitialLoading: isLoadingCards } = useFetchRecognitionCards(
    { companyId },
    enabled
  );

  const { createValue, updateValue, deleteValue } = useRecognitionValueMutations();
  const { createCard } = useRecognitionCardMutations();

  const values: ManagedRecognitionValue[] = valuesData?.list ?? [];
  const availableCards = cardsData?.list ?? [];

  const invalidateValues = () =>
    queryClient.invalidateQueries({ queryKey: ['fetchRecognitionValues', companyId] });
  const invalidateCards = () =>
    queryClient.invalidateQueries({ queryKey: ['fetchRecognitionCards', companyId] });

  const notifySaved = () =>
    toast({
      position: 'top-right',
      status: 'success',
      title: 'Saved',
      description: 'The recognition value was saved.',
    });

  const handleCreate = async (form: RecognitionValueFormState) => {
    await createValue.mutateAsync({
      companyId,
      name: form.name,
      description: form.description || null,
      emoji: form.emoji,
      allowGifs: form.allowGifs,
      enabled: form.enabled,
      cardIds: form.cardIds,
    });
    await invalidateValues();
    notifySaved();
  };

  const handleUpdate = async (valueId: string, form: RecognitionValueFormState) => {
    await updateValue.mutateAsync({
      valueId,
      body: {
        name: form.name,
        description: form.description || null,
        emoji: form.emoji,
        allowGifs: form.allowGifs,
        enabled: form.enabled,
        cardIds: form.cardIds,
      },
    });
    await invalidateValues();
    notifySaved();
  };

  const handleDelete = async (valueId: string) => {
    await deleteValue.mutateAsync(valueId);
    await invalidateValues();
    toast({
      position: 'top-right',
      status: 'success',
      title: 'Deleted',
      description: 'The recognition value was deleted.',
    });
  };

  const handleToggleEnabled = async (value: ManagedRecognitionValue, isEnabled: boolean) => {
    // Optimistically flip the toggle so the UI responds instantly, then roll back if the
    // server rejects the change. useFetchRecognitionValues keys on [name, companyId, isEnabled].
    const valuesQueryKey = ['fetchRecognitionValues', companyId, undefined];
    const previousValues = queryClient.getQueryData<RecognitionValuesResponse>(valuesQueryKey);

    queryClient.setQueryData<RecognitionValuesResponse>(valuesQueryKey, (current) =>
      current
        ? {
            ...current,
            list: current.list.map((item) =>
              item.id === value.id ? { ...item, enabled: isEnabled } : item
            ),
          }
        : current
    );

    try {
      await updateValue.mutateAsync({
        valueId: value.id,
        body: {
          name: value.name,
          description: value.description,
          emoji: value.emoji,
          allowGifs: value.allowGifs,
          enabled: isEnabled,
          cardIds: value.cards.map((card) => card.id),
        },
      });
    } catch {
      queryClient.setQueryData(valuesQueryKey, previousValues);
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'The value could not be updated. Please try again.',
      });
    }
  };

  const handleUploadCard = async (file: File) => {
    const contentUrl = await uploadRecognitionCardImage(file, companyId);
    const card = await createCard.mutateAsync({ companyId, contentUrl });
    await invalidateCards();
    return card.id;
  };

  return (
    <RecognitionValuesManager
      description="Define the values employees can recognise each other for."
      values={values}
      availableCards={availableCards}
      isLoading={isLoadingValues || isLoadingCards}
      showEnabledToggle
      isSaving={createValue.isLoading || updateValue.isLoading}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      onToggleEnabled={handleToggleEnabled}
      onUploadCard={handleUploadCard}
    />
  );
}

export default CompanyValuesSection;
